/**
 * PWA Service - Handles service worker registration, updates, and PWA features
 */

// Service Worker Registration State
interface SWRegistrationState {
  registration: ServiceWorkerRegistration | null;
  isInstalled: boolean;
  isUpdateAvailable: boolean;
  isOffline: boolean;
}

const state: SWRegistrationState = {
  registration: null,
  isInstalled: false,
  isUpdateAvailable: false,
  isOffline: !navigator.onLine,
};

// Event listeners for state changes
type StateChangeCallback = (state: SWRegistrationState) => void;
const stateListeners: Set<StateChangeCallback> = new Set();

function notifyStateChange() {
  stateListeners.forEach((callback) => callback({ ...state }));
}

// Subscribe to state changes
export function subscribeToSWState(callback: StateChangeCallback): () => void {
  stateListeners.add(callback);
  callback({ ...state }); // Immediately invoke with current state
  return () => stateListeners.delete(callback);
}

// Register service worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    state.registration = registration;
    state.isInstalled = !!registration.active;
    notifyStateChange();

    console.log('[PWA] Service Worker registered:', registration.scope);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New content available
          state.isUpdateAvailable = true;
          notifyStateChange();
          console.log('[PWA] New content available, refresh to update');
        }
      });
    });

    // Check for updates periodically (every 60 minutes)
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    // Check for updates on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration.update();
      }
    });

    return registration;
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error);
    return null;
  }
}

// Force update and reload
export async function applyUpdate(): Promise<void> {
  if (!state.registration?.waiting) {
    console.warn('[PWA] No update waiting');
    return;
  }

  // Tell the waiting service worker to skip waiting
  state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });

  // Reload once the new service worker takes over
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}

// Check if app is installed as PWA
export function isInstalledPWA(): boolean {
  // Check display mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Check if running in TWA (Trusted Web Activity)
  if (document.referrer.includes('android-app://')) {
    return true;
  }

  // iOS Safari check
  if ((navigator as any).standalone === true) {
    return true;
  }

  return false;
}

// Online/offline handling
export function setupConnectivityListeners(): void {
  const updateOnlineStatus = () => {
    state.isOffline = !navigator.onLine;
    notifyStateChange();

    if (navigator.onLine) {
      console.log('[PWA] Back online');
      // Trigger background sync
      triggerBackgroundSync();
    } else {
      console.log('[PWA] Gone offline');
    }
  };

  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
}

// Background sync helpers
export async function triggerBackgroundSync(): Promise<void> {
  if (!state.registration) return;

  try {
    // Request sync for all pending data
    if ('sync' in state.registration) {
      await (state.registration as any).sync.register('sync-flashcards');
      await (state.registration as any).sync.register('sync-notes');
      await (state.registration as any).sync.register('sync-whiteboard');
      await (state.registration as any).sync.register('sync-progress');
      console.log('[PWA] Background sync registered');
    }
  } catch (error) {
    console.warn('[PWA] Background sync not available:', error);
  }
}

// Queue data for background sync
export async function queueForSync(type: 'flashcards' | 'notes' | 'whiteboard' | 'progress', data: any): Promise<void> {
  const db = await openSyncDB();
  await addPendingSync(db, type, data);

  // Try to sync immediately if online
  if (navigator.onLine) {
    await triggerBackgroundSync();
  }
}

// IndexedDB helpers for sync queue
function openSyncDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('AbsorbitSyncDB', 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('pendingSync')) {
        const store = db.createObjectStore('pendingSync', { keyPath: 'id', autoIncrement: true });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

function addPendingSync(db: IDBDatabase, type: string, data: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['pendingSync'], 'readwrite');
    const store = transaction.objectStore('pendingSync');
    const request = store.add({
      type,
      data,
      timestamp: Date.now(),
    });

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Push notification helpers
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[PWA] Notifications not supported');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  console.log('[PWA] Notification permission:', permission);
  return permission;
}

export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!state.registration) {
    console.warn('[PWA] No service worker registration');
    return null;
  }

  try {
    // Get the VAPID public key from the server
    const response = await fetch('/api/push/vapid-public-key');
    if (!response.ok) {
      throw new Error('Failed to get VAPID public key');
    }
    const { publicKey } = await response.json();

    const subscription = await state.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    // Send subscription to server
    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
    });

    console.log('[PWA] Push subscription successful');
    return subscription;
  } catch (error) {
    console.error('[PWA] Push subscription failed:', error);
    return null;
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  if (!state.registration) return false;

  try {
    const subscription = await state.registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();

      // Notify server
      await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });

      console.log('[PWA] Push unsubscribed');
      return true;
    }
  } catch (error) {
    console.error('[PWA] Push unsubscribe failed:', error);
  }
  return false;
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!state.registration) return null;
  try {
    return await state.registration.pushManager.getSubscription();
  } catch (error) {
    console.warn('[PWA] Could not get push subscription:', error);
    return null;
  }
}

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Show local notification
export async function showNotification(title: string, options?: NotificationOptions): Promise<void> {
  if (!state.registration) return;

  if (Notification.permission !== 'granted') {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') return;
  }

  await state.registration.showNotification(title, {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [100, 50, 100],
    ...options,
  });
}

// Cache management
export async function clearAllCaches(): Promise<void> {
  if (!state.registration?.active) return;

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = () => resolve();
    state.registration!.active!.postMessage({ type: 'CLEAR_CACHE' }, [messageChannel.port2]);
  });
}

export async function getCacheStatus(): Promise<Record<string, { itemCount: number; urls: string[] }>> {
  if (!state.registration?.active) return {};

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event) => resolve(event.data);
    state.registration!.active!.postMessage({ type: 'GET_CACHE_STATUS' }, [messageChannel.port2]);
  });
}

export async function precacheUrls(urls: string[]): Promise<void> {
  if (!state.registration?.active) return;

  state.registration.active.postMessage({ type: 'CACHE_URLS', urls });
}

// Periodic background sync (for browsers that support it)
export async function registerPeriodicSync(): Promise<void> {
  if (!state.registration) return;

  try {
    if ('periodicSync' in state.registration) {
      const status = await navigator.permissions.query({
        name: 'periodic-background-sync' as PermissionName,
      });

      if (status.state === 'granted') {
        await (state.registration as any).periodicSync.register('refresh-content', {
          minInterval: 24 * 60 * 60 * 1000, // 24 hours
        });
        console.log('[PWA] Periodic sync registered');
      }
    }
  } catch (error) {
    console.warn('[PWA] Periodic sync not available:', error);
  }
}

// Share API integration
export async function canShare(): Promise<boolean> {
  return 'share' in navigator;
}

export async function shareContent(data: ShareData): Promise<boolean> {
  if (!('share' in navigator)) return false;

  try {
    await navigator.share(data);
    return true;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('[PWA] Share failed:', error);
    }
    return false;
  }
}

// File System Access API (for supported browsers)
export async function canAccessFileSystem(): Promise<boolean> {
  return 'showOpenFilePicker' in window;
}

// Badging API (for supported browsers)
export async function setBadge(count: number): Promise<void> {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count);
      } else {
        await (navigator as any).clearAppBadge();
      }
    } catch (error) {
      console.warn('[PWA] Badge API not available:', error);
    }
  }
}

export async function clearBadge(): Promise<void> {
  if ('clearAppBadge' in navigator) {
    try {
      await (navigator as any).clearAppBadge();
    } catch (error) {
      console.warn('[PWA] Badge API not available:', error);
    }
  }
}

// Window Controls Overlay API (for desktop PWAs)
export function supportsWindowControlsOverlay(): boolean {
  return 'windowControlsOverlay' in navigator;
}

export function getWindowControlsOverlayRect(): DOMRect | null {
  if (!supportsWindowControlsOverlay()) return null;
  return (navigator as any).windowControlsOverlay.getTitlebarAreaRect();
}

// Initialize PWA features
export async function initializePWA(): Promise<void> {
  // Register service worker
  await registerServiceWorker();

  // Setup connectivity listeners
  setupConnectivityListeners();

  // Register periodic sync
  await registerPeriodicSync();

  // Listen for messages from service worker
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data.type === 'SHARE_TARGET_DATA') {
      // Handle shared data
      window.dispatchEvent(new CustomEvent('pwa-share-target', { detail: event.data.data }));
    }
  });

  console.log('[PWA] Initialized');
}

// Get current state
export function getPWAState(): SWRegistrationState {
  return { ...state };
}
